/**
 * Smart Jina Extraction Service - VERSION AMÉLIORÉE
 * =================================
 * Service générique et production-ready pour améliorer les extractions Jina
 * 
 * ✨ NOUVELLES FONCTIONNALITÉS:
 * ✅ Sauvegarde automatique dans Firebase + FAISS via DocumentService
 * ✅ Extraction intelligente avec plusieurs stratégies
 * ✅ Filtrage basé sur la pertinence
 * ✅ Post-traitement automatique avec GPT (via askGPT)
 * ✅ Gestion d'erreurs robuste
 * ✅ Métriques et logging
 * ✅ Cache pour optimisation
 */

import { askGPT } from './OpenAIService';

class JinaAIExtract {
  constructor(options = {}) {
    this.apiBaseUrl = options.apiBaseUrl || 'http://localhost:8080';
    this.useGPTPostProcessing = options.useGPTPostProcessing !== false;
    this.maxExtractionSize = options.maxExtractionSize || 50000; // caractères
    this.timeout = options.timeout || 30000; // ms
    this.retryAttempts = options.retryAttempts || 2;
    this.cache = new Map();
    
    // ✨ NOUVEAU : Configuration pour la sauvegarde automatique
    this.autoSave = options.autoSave !== false;
    this.userId = options.userId || null; // Requis pour la sauvegarde
    this.objectiveId = options.objectiveId || 'default'; // Optionnel
    
    this.metrics = {
      totalExtractions: 0,
      successfulExtractions: 0,
      failedExtractions: 0,
      gptPostProcessing: 0,
      savedToFirebase: 0,
      totalTime: 0
    };
  }

  /**
   * Déterminer les mots-clés pertinents à partir d'une requête
   * Logique générique pour tout type de question
   */
  deriveKeywords(query) {
    // Mots vides français et anglais
    const stopwords = new Set([
      'le', 'la', 'les', 'de', 'du', 'des', 'un', 'une',
      'et', 'ou', 'mais', 'est', 'sont', 'a', 'aux', 'qu',
      'the', 'a', 'an', 'and', 'or', 'but', 'is', 'are',
      'pour', 'avec', 'sans', 'sur', 'dans', 'en', 'que',
      'quel', 'quoi', 'comment', 'pourquoi', 'où', 'quand'
    ]);

    const keywords = query
      .toLowerCase()
      .split(/[\s\-_]+/)
      .filter(word => {
        // Garder les mots significatifs (>2 car, pas stopwords)
        return word.length > 2 && !stopwords.has(word);
      })
      .filter((word, index, self) => self.indexOf(word) === index); // Uniques

    return keywords;
  }

  /**
   * Filtrer le contenu extrait en gardant les parties pertinentes
   * @param {string} content - Contenu extrait brut
   * @param {string[]} keywords - Mots-clés pertinents
   * @param {number} contextLines - Nombre de lignes de contexte autour des matches
   * @returns {string} Contenu filtré
   */
  filterContentByKeywords(content, keywords, contextLines = 7) {
    if (!content || !keywords || keywords.length === 0) {
      return content; // Pas de filtrage si pas de keywords
    }

    const lines = content.split('\n');
    const relevantIndices = new Set();

    // Identifier les lignes contenant les mots-clés
    lines.forEach((line, index) => {
      const lowerLine = line.toLowerCase();
      
      // Vérifier si cette ligne contient un mot-clé
      const hasKeyword = keywords.some(kw => 
        lowerLine.includes(kw) && lowerLine.length > 10
      );

      if (hasKeyword) {
        // Marquer cette ligne et son contexte
        for (let i = Math.max(0, index - contextLines); 
             i < Math.min(lines.length, index + contextLines + 1); 
             i++) {
          relevantIndices.add(i);
        }
      }
    });

    // Vérifier si au moins 5% du contenu a été trouvé
    if (relevantIndices.size < Math.max(5, lines.length * 0.05)) {
      // Pas assez de matches, retourner le contenu original
      return content;
    }

    // Reconstruire le contenu filtré
    const sortedIndices = Array.from(relevantIndices).sort((a, b) => a - b);
    const filteredLines = sortedIndices.map(i => lines[i]);

    return filteredLines.join('\n');
  }

  /**
   * Nettoyer le contenu extrait du bruit
   * Supprime navigations, images, répétitions, etc.
   */
  cleanContent(content) {
    return content
      // Supprimer les références aux images
      .replace(/!\[Image \d+[^\]]*\]\([^)]+\)/g, '')
      // Supprimer les liens de navigation standards
      .replace(/\[\d+\]\s*\./g, '')
      // Supprimer les références croisées [1], [2], etc.
      .replace(/\[\d+\]/g, '')
      // Supprimer les espacements multiples (>2 retours à la ligne)
      .replace(/\n{3,}/g, '\n\n')
      // Trimmer début/fin
      .trim();
  }

  /**
   * Extraire les sections structurées (headings, paragraphes)
   * Utile pour comprendre la structure du contenu
   */
  extractStructure(content) {
    const lines = content.split('\n');
    const structure = {
      mainTitle: '',
      sections: []
    };

    let currentSection = null;

    for (const line of lines) {
      if (line.startsWith('# ')) {
        structure.mainTitle = line.replace(/^# /, '').trim();
      } else if (line.startsWith('## ')) {
        currentSection = {
          heading: line.replace(/^## /, '').trim(),
          level: 2,
          content: []
        };
        structure.sections.push(currentSection);
      } else if (line.startsWith('### ')) {
        currentSection = {
          heading: line.replace(/^### /, '').trim(),
          level: 3,
          content: []
        };
        structure.sections.push(currentSection);
      } else if (currentSection && line.trim()) {
        currentSection.content.push(line);
      }
    }

    return structure;
  }

  /**
   * Calculer le score de pertinence d'un contenu par rapport aux keywords
   */
  calculateRelevanceScore(content, keywords) {
    if (!keywords || keywords.length === 0) return 0;

    const lowerContent = content.toLowerCase();
    let score = 0;

    for (const keyword of keywords) {
      const regex = new RegExp(`\\b${keyword}\\b`, 'gi');
      const matches = (lowerContent.match(regex) || []).length;
      score += matches;
    }

    return score;
  }

  /**
   * Post-traiter avec GPT pour distiller la réponse
   * ATTENTION: Appelle askGPT qui nécessite l'authentification Firebase
   */
  async postProcessWithGPT(extractedContent, originalQuery) {
    if (!this.useGPTPostProcessing) {
      return extractedContent;
    }

    try {
      const prompt = `
Tu es un assistant de recherche spécialisé dans l'extraction d'informations pertinentes.

QUESTION ORIGINALE: "${originalQuery}"

CONTENU EXTRAIT:
${extractedContent}

TÂCHE:
1. Identifie les parties du contenu qui répondent directement à la question
2. Élimine le bruit et les informations non pertinentes
3. Résume et structure la réponse de manière claire
4. Si l'information demandée n'est pas présente, indique-le clairement

CRITÈRES:
- Reste fidèle aux informations du contenu
- Sois concis mais complet
- Utilise le même format/structure que le contenu original
- Si c'est une question spécifique (prix, date, contact), mets-le en avant

RÉPONSE:
`;

      console.log('[SmartJina] Post-traitement GPT en cours...');
      this.metrics.gptPostProcessing++;

      const gptResponse = await askGPT(prompt);
      
      if (gptResponse && !gptResponse.includes('Erreur') && !gptResponse.includes('manquant')) {
        return gptResponse;
      } else {
        console.warn('[SmartJina] GPT post-processing échoué, retour au contenu filtré');
        return extractedContent;
      }
    } catch (error) {
      console.error('[SmartJina] Erreur post-traitement GPT:', error);
      // Fallback: retourner le contenu filtré
      return extractedContent;
    }
  }

  /**
   * ✨ NOUVELLE MÉTHODE : Créer des chunks sémantiques à partir du contenu
   * Compatible avec le format attendu par DocumentService.saveWebSource()
   */
  createChunksFromContent(content, metadata = {}) {
    const chunkSize = 1000; // Taille des chunks en caractères
    const chunkOverlap = 200; // Chevauchement entre chunks
    
    const chunks = [];
    const lines = content.split('\n');
    let currentChunk = '';
    let chunkIndex = 0;
    
    for (const line of lines) {
      if (currentChunk.length + line.length > chunkSize && currentChunk.length > 0) {
        // Sauvegarder le chunk actuel
        chunks.push({
          pageContent: currentChunk.trim(),
          metadata: {
            ...metadata,
            chunkIndex: chunkIndex++,
            chunkSize: currentChunk.length
          }
        });
        
        // Commencer un nouveau chunk avec chevauchement
        const words = currentChunk.split(' ');
        const overlapWords = words.slice(-Math.floor(chunkOverlap / 5)); // Approximatif
        currentChunk = overlapWords.join(' ') + '\n' + line;
      } else {
        currentChunk += (currentChunk ? '\n' : '') + line;
      }
    }
    
    // Ajouter le dernier chunk
    if (currentChunk.trim()) {
      chunks.push({
        pageContent: currentChunk.trim(),
        metadata: {
          ...metadata,
          chunkIndex: chunkIndex++,
          chunkSize: currentChunk.length
        }
      });
    }
    
    return chunks;
  }

  /**
   * ✨ NOUVELLE MÉTHODE : Sauvegarder le résultat dans Firebase + FAISS
   * Utilise la nouvelle route backend /api/jina/save
   */
  async saveToFirebase(extractionResult, additionalMetadata = {}) {
    if (!this.userId) {
      console.warn('[SmartJina] userId manquant, impossible de sauvegarder dans Firebase');
      return { success: false, error: 'userId required' };
    }

    try {
      console.log('[SmartJina] 💾 Sauvegarde dans Firebase + FAISS...');
      
      // Créer les chunks à partir du contenu final
      const chunks = this.createChunksFromContent(extractionResult.content, {
        query: extractionResult.query,
        sourceUrl: extractionResult.sourceUrl,
        extractedAt: new Date().toISOString(),
        ...additionalMetadata
      });
      
      // Préparer les données pour saveWebSource
      const webSourceData = {
        chunks,
        metadata: {
          title: extractionResult.structure.mainTitle || `Extraction: ${extractionResult.query}`,
          description: `Extraction web pour la requête: ${extractionResult.query}`,
          source: extractionResult.sourceUrl,
          sources: [extractionResult.sourceUrl],
          query: extractionResult.query,
          objectiveId: this.objectiveId,
          userId: this.userId,
          sectionCount: extractionResult.structure.sections.length,
          relevanceScore: extractionResult.relevanceScore,
          keywords: extractionResult.keywords,
          extractionStats: {
            rawLength: extractionResult.rawLength,
            filteredLength: extractionResult.filteredLength,
            finalLength: extractionResult.finalLength,
            compressionRatio: extractionResult.compressed.compressionRatio,
            processingTime: extractionResult.processingTime
          },
          ...additionalMetadata
        }
      };
      
      // Appeler la route backend pour sauvegarder
      const response = await fetch(`${this.apiBaseUrl}/api/jina/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAuthToken()}` // Token Firebase
        },
        body: JSON.stringify(webSourceData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Save failed: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.success) {
        this.metrics.savedToFirebase++;
        console.log(`[SmartJina] ✅ Sauvegardé avec succès - Document ID: ${result.documentId}`);
        return {
          success: true,
          documentId: result.documentId,
          chunkCount: result.chunkCount,
          vectorIds: result.vectorIds
        };
      } else {
        throw new Error(result.error || 'Save failed');
      }
      
    } catch (error) {
      console.error('[SmartJina] ❌ Erreur sauvegarde Firebase:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Helper pour obtenir le token Firebase
   * À adapter selon votre implémentation d'authentification
   */
  getAuthToken() {
    // Cette méthode doit être adaptée selon votre gestion d'authentification
    // Exemple avec Firebase Auth côté client:
    if (typeof window !== 'undefined' && window.firebase) {
      const user = window.firebase.auth().currentUser;
      if (user) {
        return user.getIdToken();
      }
    }
    
    // Si pas d'accès au token, retourner null
    console.warn('[SmartJina] Impossible de récupérer le token Firebase');
    return null;
  }

  /**
   * Pipeline principal: recherche complète avec réponse intelligente
   * ✨ MISE À JOUR : Intègre la sauvegarde automatique si activée
   */
  async findAnswer(query, targetUrl, options = {}) {
    const startTime = Date.now();
    this.metrics.totalExtractions++;

    console.log(`[SmartJina] Recherche: "${query}" sur ${targetUrl}`);

    try {
      // Étape 1: Dériver les mots-clés
      const keywords = this.deriveKeywords(query);
      console.log(`[SmartJina] Keywords détectés: ${keywords.join(', ')}`);

      if (keywords.length === 0) {
        console.warn('[SmartJina] Aucun mot-clé pertinent détecté');
      }

      // Étape 2: Vérifier le cache
      const cacheKey = `${targetUrl}|${keywords.join(',')}`;
      if (this.cache.has(cacheKey)) {
        console.log('[SmartJina] Résultat trouvé en cache');
        return this.cache.get(cacheKey);
      }

      // Étape 3: Extraire le contenu via Jina
      const rawContent = await this.extractFromJina(targetUrl, options);

      if (!rawContent) {
        throw new Error('Extraction Jina échouée ou contenu vide');
      }

      console.log(`[SmartJina] Contenu extrait: ${rawContent.length} caractères`);

      // Étape 4: Nettoyer
      let processedContent = this.cleanContent(rawContent);
      console.log(`[SmartJina] Après nettoyage: ${processedContent.length} caractères`);

      // Étape 5: Filtrer par mots-clés
      const filteredContent = this.filterContentByKeywords(
        processedContent,
        keywords,
        options.contextLines || 7
      );
      console.log(`[SmartJina] Après filtrage: ${filteredContent.length} caractères`);

      // Étape 6: Extraire la structure pour analyse
      const structure = this.extractStructure(filteredContent);
      const relevanceScore = this.calculateRelevanceScore(filteredContent, keywords);

      // Étape 7: Post-traitement avec GPT si le contenu filtré est encore grand
      let finalContent = filteredContent;
      if (filteredContent.length > 3000 && options.useGPTPostProcessing !== false) {
        finalContent = await this.postProcessWithGPT(filteredContent, query);
      }

      // Résultat final
      const result = {
        success: true,
        query,
        sourceUrl: targetUrl,
        keywords,
        rawLength: rawContent.length,
        filteredLength: filteredContent.length,
        finalLength: finalContent.length,
        content: finalContent,
        structure,
        relevanceScore,
        compressed: this.getCompressionStats(rawContent.length, finalContent.length),
        processingTime: Date.now() - startTime,
        metadata: {
          formattedWithGPT: filteredContent.length > 3000,
          cached: false
        }
      };

      // ✨ NOUVEAU : Sauvegarde automatique si activée
      if (this.autoSave && this.userId) {
        console.log('[SmartJina] 💾 Auto-sauvegarde activée...');
        const saveResult = await this.saveToFirebase(result, options.saveMetadata || {});
        result.saveResult = saveResult;
        
        if (saveResult.success) {
          result.documentId = saveResult.documentId;
          console.log(`[SmartJina] ✅ Auto-sauvegarde réussie - Document ID: ${saveResult.documentId}`);
        }
      }

      // Mettre en cache
      this.cache.set(cacheKey, result);

      // Mettre à jour les métriques
      this.metrics.successfulExtractions++;
      this.metrics.totalTime += result.processingTime;

      console.log(`[SmartJina] ✅ Succès en ${result.processingTime}ms`);
      return result;

    } catch (error) {
      this.metrics.failedExtractions++;
      console.error('[SmartJina] ❌ Erreur:', error);

      return {
        success: false,
        query,
        error: error.message,
        suggestions: this.generateFallbackSuggestions(query, targetUrl),
        processingTime: Date.now() - startTime
      };
    }
  }

  /**
   * Extraire via l'API Jina (via votre backend)
   */
  async extractFromJina(url, options = {}) {
    try {
      const response = await fetch(`${this.apiBaseUrl}/api/jina/extract`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          url,
          timeout: options.timeout || this.timeout,
          withImages: options.withImages || false
        })
      });

      if (!response.ok) {
        throw new Error(`Jina API error: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Extraction failed');
      }

      return data.content || '';
    } catch (error) {
      console.error('[SmartJina] Jina extraction error:', error);
      throw error;
    }
  }

  /**
   * Calculer les statistiques de compression
   */
  getCompressionStats(originalSize, finalSize) {
    const ratio = ((1 - finalSize / originalSize) * 100).toFixed(1);
    return {
      compressionRatio: `${ratio}%`,
      originalSize: `${(originalSize / 1024).toFixed(1)} KB`,
      finalSize: `${(finalSize / 1024).toFixed(1)} KB`,
      saved: `${((originalSize - finalSize) / 1024).toFixed(1)} KB`
    };
  }

  /**
   * Générer des suggestions de fallback en cas d'erreur
   */
  generateFallbackSuggestions(query, targetUrl) {
    return [
      `Vérifiez que l'URL est accessible: ${targetUrl}`,
      `Essayez une recherche plus spécifique sur le site`,
      `Cherchez une page dédiée (ex: /pricing, /tarif, /contact)`,
      `Contactez le support du site directement`,
      `Vérifiez votre connexion Internet`
    ];
  }

  /**
   * Obtenir les statistiques du service
   */
  getMetrics() {
    const avgTime = this.metrics.totalExtractions > 0
      ? (this.metrics.totalTime / this.metrics.totalExtractions).toFixed(0)
      : 0;

    return {
      ...this.metrics,
      successRate: this.metrics.totalExtractions > 0
        ? ((this.metrics.successfulExtractions / this.metrics.totalExtractions) * 100).toFixed(1)
        : 0,
      averageProcessingTime: `${avgTime}ms`,
      cacheSize: this.cache.size,
      autoSaveEnabled: this.autoSave,
      userId: this.userId ? '***' : 'not set'
    };
  }

  /**
   * Vider le cache
   */
  clearCache() {
    this.cache.clear();
    console.log('[SmartJina] Cache vidé');
  }

  /**
   * Réinitialiser les métriques
   */
  resetMetrics() {
    this.metrics = {
      totalExtractions: 0,
      successfulExtractions: 0,
      failedExtractions: 0,
      gptPostProcessing: 0,
      savedToFirebase: 0,
      totalTime: 0
    };
    console.log('[SmartJina] Métriques réinitialisées');
  }

  /**
   * ✨ NOUVELLE MÉTHODE : Configurer userId/objectiveId après instanciation
   */
  configure(config) {
    if (config.userId !== undefined) {
      this.userId = config.userId;
    }
    if (config.objectiveId !== undefined) {
      this.objectiveId = config.objectiveId;
    }
    if (config.autoSave !== undefined) {
      this.autoSave = config.autoSave;
    }
    console.log('[SmartJina] Configuration mise à jour:', {
      userId: this.userId ? '***' : 'not set',
      objectiveId: this.objectiveId,
      autoSave: this.autoSave
    });
  }
}

// Export singleton pour utilisation simplifiée
export const smartJinaService = new JinaAIExtract();

// Export classe pour instanciation personnalisée
export default JinaAIExtract;